"use client";

import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useMemo } from "react";

import { useOrgStore } from "@/store/org-store";
import { useLocation } from "@tanstack/react-router";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	useSidebar,
} from "shadcn-ui";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "shadcn-ui";
import { Icons } from "./icons";
import { useTheme } from "next-themes";

export function VersionSwitcher() {
	const currentOrg = useOrgStore((state) => state.currentOrg);
	const setCurrentOrg = useOrgStore((state) => state.setCurrentOrg);
	const orgs = useOrgStore((state) => state.orgs);

	const { theme } = useTheme();

	const pathname = useLocation();

	const { isMobile } = useSidebar();

	const setNav = useOrgStore((state) => state.setNav);

	useEffect(() => {
		const path = pathname.href.split("/").filter(Boolean);

		if (path[0] !== currentOrg?.key) {
			const key = orgs.find((org) => org.key === path[0]);
			if (key) {
				setCurrentOrg(key);
			}
		}
	}, [pathname]);

	const SideBarOrgSelector = useMemo(
		() => (
			<SidebarMenuButton
				size="lg"
				className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
				tooltip={`${currentOrg.name} - ${currentOrg.version}`}
				variant="switch"
			>
				<div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground shrink-0">
					{theme === "light" ? <Icons.lightLogo className="size-5" /> : <Icons.darkLogo className="size-5" />}
				</div>
				<div className="grid flex-1 text-left text-sm leading-tight">
					<span className="truncate font-semibold">{currentOrg.name}</span>
					<span className="truncate text-xs text-sidebar-foreground/70">{currentOrg.version}</span>
				</div>
				<ChevronsUpDown className="ml-auto h-4 w-4 shrink-0" />
			</SidebarMenuButton>
		),
		[currentOrg.name, currentOrg.version, theme],
	);

	const memoizedDropdownMenu = useMemo(
		() => (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>{SideBarOrgSelector}</DropdownMenuTrigger>
				<DropdownMenuContent
					className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
					align="start"
					side={isMobile ? "bottom" : "right"}
					sideOffset={4}
				>
					{orgs.map(({ version, name, key }) => (
						<DropdownMenuItem
							className="gap-2 p-2"
							key={name}
							onSelect={() => {
								setCurrentOrg({ version, name, key });
								setNav(key);
							}}
						>
							<span className="flex space-y-1 items-end gap-2">
								{name}
								<div className="text-xs opacity-70">
									{version.includes(".") ? `${version}` : version}
								</div>
							</span>
							{key === currentOrg.key && <Check className="ml-auto" />}
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>
		),
		[orgs, currentOrg, setCurrentOrg, setNav, isMobile, theme],
	);

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>{SideBarOrgSelector}</DropdownMenuTrigger>
					<DropdownMenuContent
						className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
						align="start"
						side={isMobile ? "bottom" : "right"}
						sideOffset={4}
					>
						{orgs.map(({ version, name, key }) => (
							<DropdownMenuItem
								className="gap-2 p-2"
								key={name}
								onSelect={() => {
									setCurrentOrg({ version, name, key });
									setNav(key);
									console.log("CHANGE: ", key)
								}}
							>
								<span className="flex space-y-1 items-end gap-2">
									{name}
									<div className="text-xs opacity-70">
										{version.includes(".") ? `${version}` : version}
									</div>
								</span>
								{key === currentOrg.key && <Check className="ml-auto" />}
							</DropdownMenuItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	);
}
